/**
 * Télémétrie de session. Chaque métrique a une définition écrite dans tools/telemetry/METRICS.md ;
 * ne pas ajouter de compteur sans y documenter sa sémantique (RETEX §6.7).
 */
import type { SimEvent } from '../events';

export type Outcome = 'victory' | 'defeat' | 'abandoned';

export interface SessionSummary {
  outcome: Outcome;
  simSeconds: number;
  laserShots: number;
  laserHitsEnemy: number;
  laserHitsAny: number;
  missilesLaunched: number;
  swarmLaunched: number;
  swarmSalvos: number;
  swarmTargetLosses: number;
  swarmDirectHits: number;
  swarmProximity: number;
  swarmDamageEvents: number;
  damageDealt: { laser: number; missile: number; swarm: number };
  damageReceived: Record<string, number>;
  kills: Record<string, number>;
  waveDurations: number[];
  bossPhaseStarts: Record<string, number>;
  bossPhaseDurations: Record<string, number>;
  repairsApplied: number;
  pickupsCollected: number;
  missilesGainedFromPickups: number;
  flight: {
    mobileSeconds: number;
    forwardSeconds: number;
    reverseSeconds: number;
    lateralSeconds: number;
    boostSeconds: number;
    brakeSeconds: number;
    reverseRatio: number;
  };
  spatial: {
    heavyTooCloseSeconds: number;
    rearPressureSeconds: number;
    marksmanLongRangeSeconds: number;
  };
  sectorsVisited: string[];
  score: number;
  bestMultiplier: number;
  deaths: number;
  docks: number;
  serviceSeconds: number;
  overheats: number;
  boostDepletions: number;
  droneDetonations: number;
  shieldAbsorbed: number;
  shieldBreaks: number;
  emergencyRepairs: number;
}

export interface TelemetryLogEntry {
  t: number;
  type: string;
  [key: string]: unknown;
}

/** Événements trop fréquents pour le journal (ils restent comptés). */
const NOT_LOGGED = new Set(['laserFired', 'projectileSpawned', 'projectileRemoved', 'impact', 'explosion']);

export class Telemetry {
  readonly log: TelemetryLogEntry[] = [];
  private s: SessionSummary = {
    outcome: 'abandoned',
    simSeconds: 0,
    laserShots: 0,
    laserHitsEnemy: 0,
    laserHitsAny: 0,
    missilesLaunched: 0,
    swarmLaunched: 0,
    swarmSalvos: 0,
    swarmTargetLosses: 0,
    swarmDirectHits: 0,
    swarmProximity: 0,
    swarmDamageEvents: 0,
    damageDealt: { laser: 0, missile: 0, swarm: 0 },
    damageReceived: {},
    kills: {},
    waveDurations: [],
    bossPhaseStarts: {},
    bossPhaseDurations: {},
    repairsApplied: 0,
    pickupsCollected: 0,
    missilesGainedFromPickups: 0,
    flight: { mobileSeconds: 0, forwardSeconds: 0, reverseSeconds: 0, lateralSeconds: 0, boostSeconds: 0, brakeSeconds: 0, reverseRatio: 0 },
    spatial: { heavyTooCloseSeconds: 0, rearPressureSeconds: 0, marksmanLongRangeSeconds: 0 },
    sectorsVisited: [],
    score: 0,
    bestMultiplier: 1,
    deaths: 0,
    docks: 0,
    serviceSeconds: 0,
    overheats: 0,
    boostDepletions: 0,
    droneDetonations: 0,
    shieldAbsorbed: 0,
    shieldBreaks: 0,
    emergencyRepairs: 0,
  };
  private waveStart = -1;

  /** `startTime` : temps de simulation du début de la mission (monde persistant). */
  constructor(private readonly startTime = 0) {}

  private bossPhase: string | null = null;
  private bossPhaseStart = 0;

  onEvent(e: SimEvent): void {
    if (!NOT_LOGGED.has(e.type)) this.log.push(compact(e));
    const s = this.s;
    switch (e.type) {
      case 'laserFired':
        s.laserShots++;
        break;
      case 'damage':
        if (e.source === 'player') {
          if (e.weapon === 'laser') {
            s.damageDealt.laser += e.applied;
          } else if (e.weapon === 'missile') {
            s.damageDealt.missile += e.applied;
          } else if (e.weapon === 'swarm') {
            s.damageDealt.swarm += e.applied;
            s.swarmDamageEvents++;
          }
          if (e.killed) s.kills[e.targetKind] = (s.kills[e.targetKind] ?? 0) + 1;
        } else if (e.targetKind === 'player') {
          s.damageReceived[e.source] = (s.damageReceived[e.source] ?? 0) + e.applied;
          s.shieldAbsorbed += e.absorbed;
        }
        break;
      case 'missileLaunched':
        if (e.kind === 'missile') s.missilesLaunched += e.count;
        else { s.swarmLaunched += e.count; s.swarmSalvos++; }
        break;
      case 'swarmLost': s.swarmTargetLosses++; break;
      case 'swarmDirectHit': s.swarmDirectHits++; break;
      case 'swarmProximity': s.swarmProximity++; break;
      case 'waveStarted': this.waveStart = e.time; break;
      case 'waveCleared':
        if (this.waveStart >= 0) s.waveDurations.push(round(e.time - this.waveStart));
        this.waveStart = -1;
        break;
      case 'bossSpawned':
        this.bossPhaseStart = e.time;
        break;
      case 'bossPhase':
        this.closeBossPhase(e.time);
        this.bossPhase = e.phase;
        this.bossPhaseStart = e.time;
        s.bossPhaseStarts[e.phase] = round(e.time);
        break;
      case 'repair': s.repairsApplied += e.amount; break;
      case 'pickupCollected':
        s.pickupsCollected++;
        s.missilesGainedFromPickups += e.gained;
        break;
      case 'missionEnded':
        this.closeBossPhase(e.time);
        s.outcome = e.outcome;
        break;
      case 'score':
        s.score = e.total;
        s.bestMultiplier = Math.max(s.bestMultiplier, e.multiplier);
        break;
      case 'playerDown': s.deaths++; break;
      case 'dock': if (e.phase === 'docked') s.docks++; break;
      case 'overheat': s.overheats++; break;
      case 'boostDepleted': s.boostDepletions++; break;
      case 'droneDetonated': s.droneDetonations++; break;
      case 'shieldBroken': s.shieldBreaks++; break;
      case 'emergencyRepair': s.emergencyRepairs++; break;
      case 'sectorEntered':
        if (!s.sectorsVisited.includes(e.sector)) s.sectorsVisited.push(e.sector);
        break;
      default:
        break;
    }
  }

  private closeBossPhase(time: number): void {
    if (this.bossPhase) {
      this.s.bossPhaseDurations[this.bossPhase] = round(time - this.bossPhaseStart);
      this.bossPhase = null;
    }
  }

  recordLaserHit(enemy: boolean): void {
    this.s.laserHitsAny++;
    if (enemy) this.s.laserHitsEnemy++;
  }

  /** Échantillon de vol (un par tick). Seuil mobile : 3 m/s (gamedesign §14). */
  sampleFlight(dt: number, speed: number, signedForward: number, boosting: boolean, braking: boolean): void {
    const f = this.s.flight;
    if (boosting) f.boostSeconds += dt;
    if (braking) f.brakeSeconds += dt;
    if (speed < 3) return;
    f.mobileSeconds += dt;
    if (Math.abs(signedForward) >= speed * 0.5) {
      if (signedForward >= 0) f.forwardSeconds += dt;
      else f.reverseSeconds += dt;
    } else {
      f.lateralSeconds += dt;
    }
  }

  sampleSpatial(dt: number, heavyTooClose: boolean, rearPressure: boolean, marksmanLongRange: boolean): void {
    const sp = this.s.spatial;
    if (heavyTooClose) sp.heavyTooCloseSeconds += dt;
    if (rearPressure) sp.rearPressureSeconds += dt;
    if (marksmanLongRange) sp.marksmanLongRangeSeconds += dt;
  }

  recordService(dt: number): void {
    this.s.serviceSeconds += dt;
  }

  setSimSeconds(t: number): void {
    this.s.simSeconds = t - this.startTime;
  }

  summary(): SessionSummary {
    const copy = structuredClone(this.s);
    const f = copy.flight;
    const moving = f.forwardSeconds + f.reverseSeconds + f.lateralSeconds;
    f.reverseRatio = moving > 0 ? round(f.reverseSeconds / moving, 4) : 0;
    for (const key of Object.keys(f) as (keyof typeof f)[]) f[key] = round(f[key], 4);
    for (const key of Object.keys(copy.spatial) as (keyof typeof copy.spatial)[]) copy.spatial[key] = round(copy.spatial[key], 4);
    copy.simSeconds = round(copy.simSeconds);
    copy.serviceSeconds = round(copy.serviceSeconds);
    copy.shieldAbsorbed = round(copy.shieldAbsorbed);
    return copy;
  }
}

const round = (v: number, digits = 3) => Number(v.toFixed(digits));

function compact(e: SimEvent): TelemetryLogEntry {
  const { type, time, ...rest } = e;
  return { t: round(time), type, ...rest };
}
